import React, { useEffect, useRef, useState } from 'react';
import { CostCap } from '../../types';
import { CostCapInput } from '../../lib/api';
import { formatCurrency, formatCapRange } from '../../utils/formatters';
import './CostCaps.css';

/** Dates to open the add form with (from "Cap selected" in the table). */
export interface CostCapDraft {
  start_date: string;
  end_date: string;
}

interface CostCapsProps {
  caps: CostCap[];
  error?: string | null;
  draft?: CostCapDraft | null;
  onDraftConsumed?: () => void;
  onAdd: (input: CostCapInput) => Promise<void>;
  onEdit: (id: number, input: CostCapInput) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

type FormMode = { kind: 'add' } | { kind: 'edit'; id: number };

/** Admin-only panel: lists the account's cost caps (with used/left budget) and
 *  adds, edits or deletes them. Capping happens server-side; this only edits
 *  the caps and shows what the backend reports. */
export const CostCaps: React.FC<CostCapsProps> = ({
  caps,
  error,
  draft,
  onDraftConsumed,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const [mode, setMode] = useState<FormMode | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const openForm = (next: FormMode, start: string, end: string, amt: string) => {
    setMode(next);
    setStartDate(start);
    setEndDate(end);
    setAmount(amt);
    setFormError(null);
  };

  // "Cap selected" in the table hands over a date range: open the add form
  // with it and bring the panel into view.
  useEffect(() => {
    if (!draft) return;
    openForm({ kind: 'add' }, draft.start_date, draft.end_date, '');
    panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    onDraftConsumed?.();
  }, [draft, onDraftConsumed]);

  const closeForm = () => {
    setMode(null);
    setFormError(null);
  };

  const submit = async () => {
    if (!mode) return;
    const capAmount = parseFloat(amount);
    if (!startDate || !endDate) {
      setFormError('Pick a start and end date.');
      return;
    }
    if (endDate < startDate) {
      setFormError('End date must be on or after the start date.');
      return;
    }
    if (isNaN(capAmount) || capAmount < 0) {
      setFormError('Enter a cap amount of $0 or more.');
      return;
    }
    const input = { start_date: startDate, end_date: endDate, cap_amount: capAmount };
    setBusy(true);
    setFormError(null);
    try {
      if (mode.kind === 'add') await onAdd(input);
      else await onEdit(mode.id, input);
      closeForm();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save cap');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (cap: CostCap) => {
    const label = `${formatCapRange(cap.start_date, cap.end_date)} (${formatCurrency(cap.cap_amount)})`;
    if (!window.confirm(`Delete the ${label} cap? Costs in that range go back to normal.`)) return;
    setDeletingId(cap.id);
    try {
      await onDelete(cap.id);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to delete cap');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="cost-caps" ref={panelRef}>
      <div className="cost-caps-header">
        <div>
          <h3>Cost caps</h3>
          <p className="cost-caps-hint">
            Admin only. Each cap is a budget used up in date order; clients still see normal costs.
          </p>
        </div>
        {!mode && (
          <button className="btn btn-secondary" onClick={() => openForm({ kind: 'add' }, '', '', '')}>
            Add cap
          </button>
        )}
      </div>

      {error && <p className="cost-caps-error">Couldn't load caps: {error}</p>}

      {mode && (
        <div className="cost-caps-form">
          <label>
            Start date
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label>
            End date
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
          <label>
            Cap amount ($)
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
                if (e.key === 'Escape') closeForm();
              }}
              autoFocus
            />
          </label>
          <div className="cost-caps-form-actions">
            <button className="btn btn-secondary" onClick={closeForm} disabled={busy}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={submit} disabled={busy}>
              {busy ? 'Saving…' : mode.kind === 'add' ? 'Save cap' : 'Save changes'}
            </button>
          </div>
          {formError && <p className="cost-caps-error">{formError}</p>}
        </div>
      )}

      {caps.length > 0 && (
        <table className="cost-caps-table">
          <thead>
            <tr>
              <th>Dates</th>
              <th>Cap</th>
              <th>Used</th>
              <th>Left</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {caps.map((cap) => (
              <tr key={cap.id}>
                <td>{formatCapRange(cap.start_date, cap.end_date)}</td>
                <td>{formatCurrency(cap.cap_amount)}</td>
                <td>{formatCurrency(cap.used)}</td>
                <td className={cap.left === 0 ? 'cost-caps-spent' : undefined}>
                  {formatCurrency(cap.left)}
                </td>
                <td className="cost-caps-row-actions">
                  <button
                    className="pricing-reset-btn"
                    disabled={busy || deletingId === cap.id}
                    onClick={() => openForm({ kind: 'edit', id: cap.id }, cap.start_date, cap.end_date, String(cap.cap_amount))}
                  >
                    Edit
                  </button>
                  <button
                    className="pricing-reset-btn"
                    disabled={deletingId === cap.id}
                    onClick={() => remove(cap)}
                  >
                    {deletingId === cap.id ? '…' : 'Delete'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {caps.length === 0 && !mode && !error && (
        <p className="cost-caps-empty">No caps on this account.</p>
      )}
    </div>
  );
};
